import React, { useState, useEffect, useCallback, useDeferredValue } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Card,
  CardMedia,
  Chip,
  IconButton,
  Stack,
  useTheme,
  Menu,
  MenuItem,
  Badge,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  ListItemText,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Group as GroupIcon,
  MoreVert as MoreVertIcon,
  Chat as ChatIcon,
  Map as MapIcon,
  PersonAdd as PersonAddIcon,
  CalendarToday as CalendarTodayIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Schedule as ScheduleIcon,
  Share as ShareIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
} from '@mui/icons-material';
import { format, formatDistanceToNow, differenceInCalendarDays, isBefore, isWithinInterval } from 'date-fns';
import api from '../config/api';
import EmptyState from '../components/EmptyState';
import Loading from '../components/Loading';
import ErrorState from '../components/ErrorState';
import useUnreadMessages from '../hooks/useUnreadMessages';
import { THEME_SHADOWS, THEME_RADIUS } from '../constants/theme';
import {
  DOCUMENT,
  PAGE_CONTAINER_MAX_WIDTH,
  documentPageCanvasSx,
  documentPageContainerSx,
  documentEyebrowSx,
  documentTextButtonSx,
  documentSolidButtonSx,
  documentSolidButtonSmallSx,
} from '../constants/documentTheme';
import GlobalNotificationSubscriber from '../components/GlobalNotificationSubscriber';
import TripModal from '../components/TripModal';
import PublishGuideDialog from '../components/PublishGuideDialog';
import {
  getTripPrimaryLocation,
  getTripAllLocations,
  getDisplayTripTitle,
  groupMyTripsSections,
  tripMatchesSearch,
  COMING_UP_WINDOW_MONTHS,
  parseTripDate,
  canEditTripDetails,
  isGroupTrip,
  getTripTimelineStatus,
} from '../utils/tripUtils';
import TripPassBadge from '../components/TripPassBadge';
import TripBookedBy from '../components/TripBookedBy';
import PartnerLockup from '../components/PartnerLockup';
import PartnerLogoMark from '../components/PartnerLogoMark';
import EmailForwardingCard from '../components/EmailForwardingCard';
import { isUsableImageUrl } from '../utils/itineraryItemImage';
import { isPartnerClientBrand } from '../contexts/ClientBrandingContext';
import { isTripCovered } from '../utils/tripPassUtils';
import { getFlightRouteLabel, isFlightItineraryItem } from '../utils/flightRouteUtils';
import { getFlightLegsFromItem } from '../utils/flightJourneyUtils';
import {
  getScheduleFallbackStatus,
  isTripEligibleForFlightStatus,
  liveStatusCacheKey,
  resolveFlightIataParts,
} from '../utils/liveFlightStatus';

const buildFlightLegChipDefs = (flights = []) => {
  if (!flights.length) return [];

  return flights.flatMap((flight, flightIndex) => {
    const legs = getFlightLegsFromItem(flight);
    const sourceLegs = legs.length ? legs : [null];

    return sourceLegs.map((leg, legIndex) => {
      const airline = leg?.airline || flight.transportation_data?.airline || '';
      const airlineCode = leg?.airline_code
        || leg?.airlineCode
        || flight.transportation_data?.airline_code
        || '';
      const flightNumber = leg?.flight_number
        || leg?.flightNumber
        || flight.transportation_data?.flight_number
        || '';
      const parts = resolveFlightIataParts({ airline, airlineCode, flightNumber });
      const from = leg?.departure_airport
        || flight.transportation_data?.departure_airport
        || flight.transportation_data?.origin
        || null;
      const to = leg?.arrival_airport
        || flight.transportation_data?.arrival_airport
        || flight.transportation_data?.destination
        || null;
      const routeLabel = (from && to)
        ? `${from} → ${to}`
        : (getFlightRouteLabel(flight) || parts?.flight_iata || null);
      if (!routeLabel) return null;

      const departureTime = leg?.departure_time
        || flight.transportation_data?.departure_time
        || flight.start_datetime
        || null;

      return {
        id: `${flight.id || flightIndex}-leg-${legIndex}`,
        flightNumber: routeLabel,
        airline: parts?.airline || airline,
        flightNum: parts?.flight_number || flightNumber,
        flightIata: parts?.flight_iata || null,
        departureTime,
        departureAirport: from,
        arrivalAirport: to,
        cacheKey: parts?.flight_iata
          ? liveStatusCacheKey({ flightIata: parts.flight_iata, departureDate: departureTime })
          : null,
      };
    }).filter(Boolean);
  });
};

const formatTripDateRange = (startDate, endDate) => {
  const start = parseTripDate(startDate);
  const end = parseTripDate(endDate);
  if (!start || !end) return '';
  if (start.getFullYear() === end.getFullYear()) {
    return `${format(start, 'EEE, MMM d')} – ${format(end, 'EEE, MMM d, yyyy')}`;
  }
  return `${format(start, 'EEE, MMM d, yyyy')} – ${format(end, 'EEE, MMM d, yyyy')}`;
};

const getTripDurationDays = (startDate, endDate) => {
  const start = parseTripDate(startDate);
  const end = parseTripDate(endDate);
  if (!start || !end) return 0;
  return Math.max(1, differenceInCalendarDays(end, start) + 1);
};

const getCoverBadgeSx = {
  fontWeight: 500,
  fontSize: 9.5,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  height: 22,
  borderRadius: 0,
  backgroundColor: '#C9A961',
  color: '#2A070C',
  border: 0,
  '& .MuiChip-label': { px: 1.1 },
  '& .MuiChip-icon': { color: '#2A070C !important', fontSize: '13px !important', ml: 0.5 },
};

// Safe image component with error handling
const SafeImage = ({ src, fallbackSrc, alt, sx, ...props }) => {
  // Determine initial source: use fallback if src is null, undefined, or empty string
  const initialSrc = (src && src.trim()) ? src : fallbackSrc;
  const [imgSrc, setImgSrc] = useState(initialSrc);
  const [hasError, setHasError] = useState(false);

  const handleError = () => {
    if (!hasError && fallbackSrc && imgSrc !== fallbackSrc) {
      // Primary image failed, try location-based fallback
      console.log('Image failed to load, using fallback:', fallbackSrc);
      setImgSrc(fallbackSrc);
      setHasError(true);
    }
    // If fallback also fails, keep showing it anyway (better than empty space)
  };

  useEffect(() => {
    // Reset image source when props change
    const newSrc = (src && src.trim()) ? src : fallbackSrc;
    setImgSrc(newSrc);
    setHasError(false);
  }, [src, fallbackSrc]);

  // Always ensure we have a valid image URL
  const finalSrc = imgSrc || fallbackSrc || 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=600&h=300&fit=crop&q=80';

  return (
    <CardMedia
      component="img"
      src={finalSrc}
      alt={alt}
      onError={handleError}
      sx={sx}
      {...props}
    />
  );
};

// Function to get trip image URL based on location and trip type
const getTripImageUrl = (location, tripType, tripId) => {
  // If no location or "No destination set", return a generic travel image
  if (!location || location === 'No destination set') {
    return 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=600&h=300&fit=crop&q=80';
  }

  // Extract city name from location string (handles "New York, NY" or "Paris, France" etc.)
  const cityName = location.split(',')[0].trim();
  
  // All images now use backend Search API for proper attribution
  // No curated images to ensure we can always provide photographer attribution
  
  // For unknown cities, use a deterministic photo based on city name
  const cityHash = Math.abs(cityName.split('').reduce((acc, char) => {
    return ((acc << 5) - acc) + char.charCodeAt(0);
  }, 0));
  
  // Generic travel photos (permanent Unsplash photo IDs)
  const GENERIC_TRAVEL_PHOTOS = [
    'photo-1469854523086-cc02fe5d8800',
    'photo-1476514525535-07fb3b4ae5f1',
    'photo-1500835556837-99ac94a94552',
    'photo-1506905925346-21bda4d32df4',
    'photo-1464822759023-fed622ff2c3b',
    'photo-1476900164809-ff19b8ae5968',
    'photo-1445264f58423-7a4a4e81a3a2',
    'photo-1478860409698-8707f313ee8b',
    'photo-1501785888041-af3ef285b470',
    'photo-1507525428034-b723cf961d3e',
    'photo-1519681393784-d120267933ba',
    'photo-1433086966358-54859d0ed716',
    'photo-1470252649378-9c29740c9fa8'
  ];
  
  // Pick a photo based on city hash (same city = same photo always)
  const photoIndex = cityHash % GENERIC_TRAVEL_PHOTOS.length;
  const photoId = GENERIC_TRAVEL_PHOTOS[photoIndex];
  
  return `https://images.unsplash.com/${photoId}?w=600&h=300&fit=crop&q=80`;
};

const Trips = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [showAllTrips, setShowAllTrips] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'upcoming', 'current', 'past'
  const [searchQuery, setSearchQuery] = useState('');
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  
  // Sort trips by start date (most recent first)
  const sortedTrips = [...trips].sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
  const [pendingInvites, setPendingInvites] = useState([]);
  const [inviteActionLoading, setInviteActionLoading] = useState(false);
  const [inviteActionError, setInviteActionError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingTrip, setDeletingTrip] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [bulkArchiveDialogOpen, setBulkArchiveDialogOpen] = useState(false);
  const [archivingTrip, setArchivingTrip] = useState(false);
  const [archiveError, setArchiveError] = useState('');
  const [archiveNotice, setArchiveNotice] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingTripId, setEditingTripId] = useState(null);
  const [publishGuideOpen, setPublishGuideOpen] = useState(false);
  const [publishTrip, setPublishTrip] = useState(null);
  const [tripFlights, setTripFlights] = useState({}); // Store flight data for each trip
  const [tripLiveFlightStatuses, setTripLiveFlightStatuses] = useState({}); // eslint-disable-line no-unused-vars -- populated by flight-status polling
  const [tripPlannedDays, setTripPlannedDays] = useState({});
  const [partnerModalOpen, setPartnerModalOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [partnerModalData, setPartnerModalData] = useState(null);

  // Get all trip IDs for notification subscription
  const allTripIds = trips.map(trip => trip.id);
  
  // Use centralized notification system for all trips
  // eslint-disable-next-line no-unused-vars
  const { unreadCounts, markAsRead } = useUnreadMessages(allTripIds);

  // Debug logging for notification updates
  useEffect(() => {
    console.log('📊 [Trips] Unread counts updated:', unreadCounts);
    console.log('📊 [Trips] Trip IDs subscribed:', allTripIds);
  }, [unreadCounts, allTripIds]);

  // Helper functions for backward compatibility
  const getUnreadCount = (tripId) => {
    const count = unreadCounts[tripId] || 0;
    if (count > 0) {
      console.log(`📊 [Trips] Unread count for trip ${tripId}:`, count);
    }
    return count;
  };
  


  // Real-time updates via Kafka/WebSocket eliminate need for manual refresh
  // Removed visibility/focus refresh handlers since we now use real-time messaging

  const fetchTrips = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/trips?includeArchived=${showArchived}`);
      // Sort trips by start date (soonest first)
      const sortedTrips = response.data.sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
      setTrips(sortedTrips);
    } catch (err) {
      console.error('Error fetching trips:', err);
      setError('Failed to load trips. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  const fetchInvitations = useCallback(async () => {
    try {
      console.log('[DEBUG] Fetching invitations...');
      // Pending invites stay on the active list (never archived)
      const response = await api.get('/trips');
      // If backend includes invitations, filter here. Otherwise, fetch from a dedicated endpoint if available.
      const allTrips = response.data;
      console.log('[DEBUG] All trips from API:', allTrips.map(t => ({ id: t.id, title: t.title, user_role: t.user_role, invitation_status: t.invitation_status })));
      
      // Only show trips where invitation status is specifically 'pending'
      // Do NOT include user_role === 'invited' because that includes declined invitations
      const pending = allTrips.filter(trip => trip.invitation_status === 'pending');
      console.log('[DEBUG] Filtered pending invitations:', pending.map(t => ({ id: t.id, title: t.title, user_role: t.user_role, invitation_status: t.invitation_status })));
      
      setPendingInvites(pending);
    } catch (err) {
      console.error('[DEBUG] Error fetching invitations:', err);
      setPendingInvites([]);
    }
  }, []);

  // Fetch flight data for all trips
  const fetchFlightData = useCallback(async () => {
    if (trips.length === 0) return;
    
    try {
      const flightPromises = trips.map(async (trip) => {
        try {
          const response = await api.get(`/trips/${trip.id}/itinerary`);
          const flights = response.data.filter(isFlightItineraryItem);
          const plannedDays = new Set(
            response.data
              .map((item) => item.start_date || item.date || item.day_date)
              .filter(Boolean)
              .map((value) => String(value).split('T')[0])
          ).size;
          return { tripId: trip.id, flights, plannedDays };
        } catch (error) {
          console.warn(`Failed to fetch flights for trip ${trip.id}:`, error);
          return { tripId: trip.id, flights: [], plannedDays: 0 };
        }
      });
      
      const results = await Promise.all(flightPromises);
      const flightData = {};
      const plannedData = {};
      results.forEach(({ tripId, flights, plannedDays }) => {
        flightData[tripId] = flights;
        plannedData[tripId] = plannedDays;
      });
      
      setTripFlights(flightData);
      setTripPlannedDays(plannedData);
    } catch (error) {
      console.error('Error fetching flight data:', error);
    }
  }, [trips]);

  // Live / schedule status only for active or upcoming trips (skip completed).
  // Trip cards never call Duffel/AviationStack — status is derived from booked times.
  useEffect(() => {
    const eligibleTripIds = new Set(
      trips
        .filter((trip) => isTripEligibleForFlightStatus(trip))
        .map((trip) => String(trip.id))
    );
    const tripIds = Object.keys(tripFlights).filter((tripId) => eligibleTripIds.has(String(tripId)));
    if (!tripIds.length) {
      setTripLiveFlightStatuses({});
      return undefined;
    }

    const localStatuses = {};
    tripIds.forEach((tripId) => {
      buildFlightLegChipDefs(tripFlights[tripId] || [])
        .filter((chip) => chip.flightIata && chip.cacheKey)
        .forEach((chip) => {
          localStatuses[chip.cacheKey] = getScheduleFallbackStatus(chip.departureTime);
        });
    });
    setTripLiveFlightStatuses(localStatuses);
    return undefined;
  }, [tripFlights, trips]);

  useEffect(() => {
    fetchTrips();
    fetchInvitations();
  }, [fetchTrips, fetchInvitations]);

  // Fetch flight data when trips are loaded
  useEffect(() => {
    if (trips.length > 0) {
      fetchFlightData();
    }
  }, [trips, fetchFlightData]);

  const handleRetry = () => {
    fetchTrips();
  };

  const handleMenuOpen = (event, trip) => {
    setAnchorEl(event.currentTarget);
    setSelectedTrip(trip);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTrip(null);
  };

  const handleShareTrip = async (event, trip) => {
    event.stopPropagation();
    const url = `${window.location.origin}/trips/${trip.id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: getDisplayTripTitle(trip), url });
        return;
      }
      await navigator.clipboard.writeText(url);
    } catch (err) {
      if (err?.name !== 'AbortError') {
        console.warn('Unable to share trip', err);
      }
    }
  };

  const getCoverStateLabel = (tripStatus) => {
    if (tripStatus.status === 'upcoming') {
      return `${t('trip.status.upcoming')} ${tripStatus.daysText}`;
    }
    if (tripStatus.status === 'current') {
      return tripStatus.daysText;
    }
    return `${t('trip.status.completed')} ${String(tripStatus.daysText).replace(/^Ended\s+/i, '')}`;
  };

  // Get trip status and remaining days
  const getTripStatus = (trip) => {
    const now = new Date();
    const startDate = new Date(trip.start_date);
    const endDate = new Date(trip.end_date);
    
    if (isBefore(now, startDate)) {
      const daysUntil = formatDistanceToNow(startDate, { addSuffix: true });
      return { status: 'upcoming', label: t('trip.status.upcoming'), daysText: daysUntil, color: 'primary' };
    } else if (isWithinInterval(now, { start: startDate, end: endDate })) {
      const daysLeft = formatDistanceToNow(endDate, { addSuffix: true });
      return { status: 'current', label: t('trip.status.inProgress'), daysText: `Ends ${daysLeft}`, color: 'success' };
    } else {
      const daysAgo = formatDistanceToNow(endDate, { addSuffix: true });
      return { status: 'past', label: t('trip.status.completed'), daysText: `Ended ${daysAgo}`, color: 'default' };
    }
  };

  // Split trips into created and invited
  // Filter trips by user role
  const createdTrips = trips.filter(trip => trip.user_role === 'creator');
  const pastArchivableCount = trips.filter((trip) => {
    if (trip.invitation_status === 'pending') return false;
    if (trip.user_role !== 'creator' && trip.user_role !== 'member') return false;
    return getTripTimelineStatus(trip) === 'past';
  }).length;
  const invitedTrips = trips.filter(trip => trip.user_role !== 'creator');
  const isSearching = deferredSearchQuery.trim().length > 0;
  const searchedTrips = isSearching
    ? trips.filter((trip) => tripMatchesSearch(trip, deferredSearchQuery))
    : trips;
  const tripSections = groupMyTripsSections(searchedTrips);

  // Filter trips by status (for status filter feature)
  const filterTripsByStatus = (tripsToFilter) => {
    if (statusFilter === 'all') return tripsToFilter;
    return tripsToFilter.filter(trip => getTripStatus(trip).status === statusFilter);
  };

  // Handle stat card clicks
  const handleStatClick = (type) => {
    switch (type) {
      case 'total':
        setStatusFilter('all');
        // Scroll to trips section
        setTimeout(() => {
          const tripsSection = document.getElementById('trips-section');
          if (tripsSection) {
            tripsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
        break;
      
      case 'upcoming': {
        const upcomingTrips = trips.filter(trip => getTripStatus(trip).status === 'upcoming');
        if (upcomingTrips.length === 1) {
          navigate(`/trips/${upcomingTrips[0].id}`);
        } else {
          setStatusFilter('upcoming');
          setTimeout(() => {
            const tripsSection = document.getElementById('trips-section');
            if (tripsSection) {
              tripsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 100);
        }
        break;
      }
      
      case 'inProgress': {
        const inProgressTrips = trips.filter(trip => getTripStatus(trip).status === 'current');
        if (inProgressTrips.length === 1) {
          navigate(`/trips/${inProgressTrips[0].id}`);
        } else if (inProgressTrips.length > 1) {
          setStatusFilter('current');
          setTimeout(() => {
            const tripsSection = document.getElementById('trips-section');
            if (tripsSection) {
              tripsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 100);
        }
        break;
      }
      
      case 'completed':
        setStatusFilter('past');
        setTimeout(() => {
          const tripsSection = document.getElementById('trips-section');
          if (tripsSection) {
            tripsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
        break;
      
      default:
        break;
    }
  };

  const handleAcceptInvite = async (tripId) => {
    setInviteActionLoading(true);
    setInviteActionError('');
    try {
      await api.post(`/trips/${tripId}/accept-invitation`);
      await fetchTrips();
      await fetchInvitations();
    } catch (err) {
      setInviteActionError('Failed to accept invitation.');
    } finally {
      setInviteActionLoading(false);
    }
  };

  const handleRejectInvite = async (tripId) => {
    setInviteActionLoading(true);
    setInviteActionError('');
    try {
      console.log('[DEBUG] Rejecting invitation for trip:', tripId);
      await api.post(`/trips/${tripId}/reject-invitation`);
      console.log('[DEBUG] Rejection successful, refreshing data...');
      
      // Add a small delay to ensure database update is complete
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Force refresh of data
      await fetchTrips();
      await fetchInvitations();
      console.log('[DEBUG] Data refresh complete');
    } catch (err) {
      console.error('[DEBUG] Rejection failed:', err);
      setInviteActionError('Failed to reject invitation.');
    } finally {
      setInviteActionLoading(false);
    }
  };

  // Delete trip handler
  const handleDeleteTrip = async () => {
    if (!selectedTrip) return;
    setDeletingTrip(true);
    setDeleteError('');
    try {
      await api.delete(`/trips/${selectedTrip.id}`);
      setDeleteDialogOpen(false);
      setSelectedTrip(null);
      await fetchTrips();
    } catch (err) {
      setDeleteError('Failed to delete trip.');
    } finally {
      setDeletingTrip(false);
      handleMenuClose();
    }
  };

  const handleArchiveTrip = async () => {
    if (!selectedTrip) return;
    const nextArchived = !showArchived;
    setArchivingTrip(true);
    setArchiveError('');
    try {
      await api.patch(`/trips/${selectedTrip.id}/archive`, { archived: nextArchived });
      setArchiveDialogOpen(false);
      setSelectedTrip(null);
      await fetchTrips();
    } catch (err) {
      setArchiveError(
        nextArchived ? t('trip.archiveDialog.failed') : t('trip.archiveDialog.restoreFailed')
      );
    } finally {
      setArchivingTrip(false);
    }
  };

  const handleBulkArchivePastTrips = async () => {
    setArchivingTrip(true);
    setArchiveError('');
    try {
      const response = await api.patch('/trips/archive-past');
      const archivedCount = Number(response?.data?.archivedCount || 0);
      setBulkArchiveDialogOpen(false);
      setArchiveNotice(
        archivedCount > 0
          ? t('trip.archiveDialog.bulkSuccess', { count: archivedCount })
          : t('trip.archiveDialog.bulkNone')
      );
      await fetchTrips();
    } catch (err) {
      setArchiveError(t('trip.archiveDialog.bulkFailed'));
    } finally {
      setArchivingTrip(false);
    }
  };

  const canShowTripMenu = (trip) => {
    if (!trip) return false;
    if (trip.invitation_status === 'pending') return false;
    return trip.user_role === 'creator' || trip.user_role === 'member';
  };

  if (loading) {
    return (
      <Box sx={documentPageCanvasSx}>
        <Loading message="Loading your trips..." />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={documentPageCanvasSx}>
        <ErrorState
          title="Error Loading Trips"
          message={error}
          actionLabel="Try Again"
          onAction={handleRetry}
        />
      </Box>
    );
  }

  // Categorize trips
  // Removed unused variables that were calculated but never used:
  // const now = new Date();
  // const upcomingTrips = trips.filter(trip => isBefore(now, new Date(trip.start_date)));
  // const currentTrips = trips.filter(trip => 
  //   isWithinInterval(now, { start: new Date(trip.start_date), end: new Date(trip.end_date) })
  // );
  // const pastTrips = trips.filter(trip => isAfter(now, new Date(trip.end_date)));
  // const invitedTrips = trips.filter(trip => trip.user_role === 'invited');

  // If user has no trips at all - show rich onboarding (active view only)
  if (sortedTrips.length === 0 && !showArchived) {
    return (
      <Box sx={documentPageCanvasSx}>
        <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ ...documentPageContainerSx, py: { xs: 4, md: 6 } }}>
          <Box sx={{ pb: 1.75, mb: 3, borderBottom: `1px solid ${DOCUMENT.ink}`, display: 'flex', alignItems: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
            <Typography component="h1" sx={{ fontFamily: DOCUMENT.display, fontWeight: 400, fontSize: { xs: 30, md: 42 }, lineHeight: 1, color: DOCUMENT.ink }}>
              {t('trip.pageHeading')}
            </Typography>
            <Button
              onClick={() => setShowArchived(true)}
              startIcon={<ArchiveIcon />}
              sx={{ ...documentTextButtonSx, ml: 'auto' }}
              data-testid="show-archived-toggle"
            >
              {t('trip.showArchived')}
            </Button>
          </Box>
          <EmailForwardingCard compact />
          <Box sx={{ mb: { xs: 4, md: 5 } }}>
            <Typography sx={{ fontFamily: DOCUMENT.display, fontWeight: 400, fontSize: { xs: 26, md: 32 }, mb: 1, color: DOCUMENT.ink }}>
              {t('trip.empty.heroTitle')}
            </Typography>
            <Typography sx={{ color: DOCUMENT.ink2, maxWidth: 500, mb: 3, fontSize: 15 }}>
              {t('trip.empty.heroDesc')}
            </Typography>
            <Button onClick={() => setCreateModalOpen(true)} sx={documentSolidButtonSx}>
              {t('trip.empty.createFirst')}
            </Button>
          </Box>

          <Box sx={{ p: { xs: 2.5, md: 3.5 }, mb: 3, bgcolor: DOCUMENT.surface, border: `1px solid ${DOCUMENT.ruleHard}` }}>
            <Typography sx={{ ...documentEyebrowSx, mb: 2 }}>
              {t('trip.empty.whatYouCanDo')}
            </Typography>
            <Grid container spacing={2}>
              {[
                { icon: <MapIcon sx={{ fontSize: 22 }} />, title: t('trip.empty.buildItinerary'), desc: t('trip.empty.buildItineraryDesc') },
                { icon: <GroupIcon sx={{ fontSize: 22 }} />, title: t('trip.empty.travelTogether'), desc: t('trip.empty.travelTogetherDesc') },
                { icon: <ChatIcon sx={{ fontSize: 22 }} />, title: t('trip.empty.stayConnected'), desc: t('trip.empty.stayConnectedDesc') },
                { icon: <LocationIcon sx={{ fontSize: 22 }} />, title: t('trip.empty.exploreDiscover'), desc: t('trip.empty.exploreDiscoverDesc') },
              ].map((item) => (
                <Grid item xs={12} sm={6} key={item.title}>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', py: 1 }}>
                    <Box sx={{ color: DOCUMENT.oxblood, mt: 0.25 }}>{item.icon}</Box>
                    <Box>
                      <Typography sx={{ fontWeight: 500, fontSize: '0.9rem', mb: 0.25, color: DOCUMENT.ink }}>{item.title}</Typography>
                      <Typography sx={{ fontSize: '0.8rem', lineHeight: 1.4, color: DOCUMENT.ink3 }}>{item.desc}</Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Grid container spacing={2}>
            {[
              { title: t('trip.empty.exploreDestinations'), desc: t('trip.empty.exploreDestinationsDesc'), path: '/destinations' },
              { title: t('trip.empty.browseDeals'), desc: t('trip.empty.browseDealsDesc'), path: '/deals' },
              { title: t('trip.empty.travelGuides'), desc: t('trip.empty.travelGuidesDesc'), path: '/guides' },
            ].map((item) => (
              <Grid item xs={12} sm={4} key={item.path}>
                <Paper
                  elevation={0}
                  onClick={() => navigate(item.path)}
                  sx={{
                    p: 2.5,
                    borderRadius: 0,
                    border: `1px solid ${DOCUMENT.ruleHard}`,
                    bgcolor: DOCUMENT.surface,
                    cursor: 'pointer',
                    '&:hover': { borderColor: DOCUMENT.accent },
                  }}
                >
                  <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 20, mb: 0.5, color: DOCUMENT.ink }}>{item.title}</Typography>
                  <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3 }}>{item.desc}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <TripModal
            open={createModalOpen}
            onClose={() => setCreateModalOpen(false)}
            mode="create"
            onSuccess={() => {
              fetchTrips();
            }}
          />
        </Container>
      </Box>
    );
  }

  const renderTripCard = (trip) => {
    const tripStatus = getTripStatus(trip);
    const isInvited = trip.user_role === 'invited';
    const unreadCount = getUnreadCount(trip.id) || 0;
    const tripIsUpgraded = isTripCovered(trip);
    const durationDays = getTripDurationDays(trip.start_date, trip.end_date);
    const plannedDays = tripPlannedDays[trip.id] || 0;

    const dateLine = [
      formatTripDateRange(trip.start_date, trip.end_date),
      durationDays > 0 ? t('trip.durationDays', { count: durationDays }) : null,
      plannedDays > 0 ? t('trip.plannedDays', { count: plannedDays }) : null,
    ].filter(Boolean).join(' · ');

    return (
        <Card
          data-testid={`trip-card-${trip.id}`}
          onClick={() => navigate(`/trips/${trip.id}`)}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            bgcolor: DOCUMENT.surface,
            border: `1px solid ${tripIsUpgraded ? DOCUMENT.accent : DOCUMENT.ruleHard}`,
            borderRadius: 0,
            boxShadow: 'none',
            overflow: 'hidden',
            cursor: 'pointer',
            transition: 'border-color 0.2s',
            '&:hover': { borderColor: DOCUMENT.accent },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.75, px: 1.75, py: 1.5 }}>
            <Box sx={{ width: 72, height: 72, flexShrink: 0, bgcolor: DOCUMENT.oxblood, overflow: 'hidden' }}>
              <SafeImage
                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                src={trip.image_url && trip.image_url.trim() ? trip.image_url : null}
                fallbackSrc={getTripImageUrl(
                  getTripPrimaryLocation(trip),
                  trip.trip_type,
                  trip.id
                )}
                alt={getDisplayTripTitle(trip)}
              />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1, pt: 0.15 }}>
              <Typography
                component="h3"
                sx={{
                  fontFamily: DOCUMENT.display,
                  fontWeight: 400,
                  fontSize: { xs: 18, sm: 20 },
                  lineHeight: 1.2,
                  color: DOCUMENT.ink,
                  mb: 0.2,
                }}
              >
                {getDisplayTripTitle(trip)}
              </Typography>
              {getTripAllLocations(trip) && (
                <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3, mb: 0.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {getTripAllLocations(trip)}
                </Typography>
              )}
              <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2, lineHeight: 1.4, mb: 0.25 }}>
                {dateLine}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ScheduleIcon sx={{ fontSize: 14 }} />
                {getCoverStateLabel(tripStatus)}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                {isInvited && (
                  <Chip label={t('trip.invitedBadge')} size="small" sx={getCoverBadgeSx} />
                )}
                <TripPassBadge trip={trip} variant="cover" />
                {!!trip.managed_by_partner && trip.partner_company_name && (
                  <TripBookedBy
                    variant="cover"
                    name={trip.partner_company_name}
                    logoUrl={trip.partner_logo}
                    banner={isPartnerClientBrand(trip.branding) ? trip.branding?.banner : undefined}
                    onClick={(event) => {
                      event.stopPropagation();
                      setSelectedTrip(trip);
                      setPartnerModalOpen(true);
                    }}
                  />
                )}
                {isGroupTrip(trip) && (
                  <Chip
                    icon={<GroupIcon />}
                    label={t('trip.groupTripBadge')}
                    size="small"
                    sx={getCoverBadgeSx}
                  />
                )}
              </Box>
              {canShowTripMenu(trip) && (showArchived || getTripTimelineStatus(trip) === 'past') && (
                <Button
                  data-testid={`trip-archive-button-${trip.id}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    setSelectedTrip(trip);
                    setArchiveError('');
                    setArchiveDialogOpen(true);
                  }}
                  sx={{
                    mt: 1,
                    p: 0,
                    minWidth: 0,
                    minHeight: 0,
                    color: DOCUMENT.ink2,
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    '&:hover': { background: 'none', textDecoration: 'underline', color: DOCUMENT.oxblood },
                  }}
                >
                  {showArchived ? t('trip.restoreTrip') : t('trip.archiveTrip')}
                </Button>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0, mt: -0.25 }}>
              {trip.invitation_status !== 'pending' && (
                <IconButton
                  size="small"
                  aria-label={t('common.share')}
                  onClick={(e) => handleShareTrip(e, trip)}
                  sx={{ color: DOCUMENT.ink2, width: 28, height: 28 }}
                >
                  <ShareIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
              {unreadCount > 0 && (
                <IconButton
                  size="small"
                  aria-label={t('trip.chat')}
                  onClick={(e) => { e.stopPropagation(); navigate(`/trips/${trip.id}?tab=chat`); }}
                  sx={{ color: DOCUMENT.ink2, width: 28, height: 28 }}
                >
                  <Badge
                    badgeContent={unreadCount > 99 ? '99+' : unreadCount}
                    sx={{
                      '& .MuiBadge-badge': {
                        bgcolor: '#8C2B22',
                        color: DOCUMENT.ivory,
                        fontSize: 9,
                        minWidth: 16,
                        height: 16,
                      }
                    }}
                  >
                    <ChatIcon sx={{ fontSize: 16 }} />
                  </Badge>
                </IconButton>
              )}
              {canShowTripMenu(trip) && (
                <IconButton
                  size="small"
                  aria-label={t('trip.moreActions')}
                  onClick={(e) => { e.stopPropagation(); handleMenuOpen(e, trip); }}
                  sx={{ color: DOCUMENT.ink2, width: 28, height: 28 }}
                >
                  <MoreVertIcon sx={{ fontSize: 18 }} />
                </IconButton>
              )}
            </Box>
          </Box>

          {trip.invitation_status === 'pending' && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              px: 1.75,
              pb: 1.5,
            }}>
              <Button
                onClick={(e) => { e.stopPropagation(); handleRejectInvite(trip.id); }}
                disabled={inviteActionLoading}
                sx={{ ...documentTextButtonSx, color: '#8C2B22' }}
              >
                {t('trip.rejectInvitation')}
              </Button>
              <Button
                onClick={(e) => { e.stopPropagation(); handleAcceptInvite(trip.id); }}
                disabled={inviteActionLoading}
                sx={{ ...documentSolidButtonSmallSx, ml: 'auto' }}
              >
                {t('trip.acceptInvitation')}
              </Button>
            </Box>
          )}
        </Card>
    );
  };

  const renderTripSection = (title, trips) => {
    if (trips.length === 0) return null;
    
    return (
      <Box sx={{ mt: 5, mb: { xs: 4, sm: 6 } }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 1.75,
          pb: 1.5,
          borderBottom: `1px solid ${DOCUMENT.ruleHard}`,
        }}>
          <Typography 
            component="h2" 
            sx={{ 
              fontFamily: DOCUMENT.display,
              fontWeight: 400,
              fontSize: 23,
              color: DOCUMENT.ink,
            }}
          >
            {title}
          </Typography>
          <Typography sx={{ fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}>
            {t('trip.sectionCount', { count: trips.length, total: sortedTrips.length })}
          </Typography>
        </Box>
          <Grid container spacing={{ xs: 1.25, sm: 1.5 }}>
           {(showAllTrips ? trips : trips.slice(0, 6)).map((trip) => (
             <Grid item xs={12} md={6} key={trip.id}>
               {renderTripCard(trip)}
             </Grid>
           ))}
          </Grid>
          
          {/* Show More/Less Button */}
          {trips.length > 6 && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              mt: 4,
              mb: 2
            }}>
              <Button
                onClick={() => setShowAllTrips(!showAllTrips)}
                sx={documentTextButtonSx}
              >
                {showAllTrips ? t('common.showLess') : t('trip.showAllTrips', { count: trips.length })}
              </Button>
            </Box>
          )}
      </Box>
    );
  };

  return (
    <Box sx={documentPageCanvasSx}>
    <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ ...documentPageContainerSx, py: 0 }}>
      {/* Global notification subscriber for comprehensive coverage */}
      <GlobalNotificationSubscriber />
      
      <Box sx={{
        display: 'flex',
        alignItems: { xs: 'flex-start', sm: 'flex-end' },
        gap: 2.5,
        flexWrap: 'wrap',
        pt: { xs: 4.5, md: 4.75 },
        pb: 1.9,
        borderBottom: `1px solid ${DOCUMENT.ink}`,
      }}>
        <Typography component="h1" sx={{ fontFamily: DOCUMENT.display, fontWeight: 400, fontSize: { xs: 30, md: 42 }, lineHeight: 1, color: DOCUMENT.ink }}>
          {showArchived ? t('trip.archivedHeading') : t('trip.pageHeading')}
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3, pb: 0.6 }}>
          {showArchived
            ? t('trip.archivedSummary', { total: trips.length })
            : t('trip.summaryLine', {
              total: trips.length,
              created: createdTrips.length,
              invited: invitedTrips.length,
            })}
        </Typography>
        <Box sx={{ ml: { sm: 'auto' }, width: { xs: '100%', sm: 'auto' }, pb: 0.35, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            onClick={() => setShowArchived(!showArchived)}
            startIcon={showArchived ? <UnarchiveIcon /> : <ArchiveIcon />}
            sx={{ ...documentTextButtonSx, width: { xs: '100%', sm: 'auto' } }}
            aria-pressed={showArchived}
            data-testid="show-archived-toggle"
          >
            {showArchived ? t('trip.showActive') : t('trip.showArchived')}
          </Button>
          {!showArchived && pastArchivableCount > 0 && (
            <Button
              data-testid="archive-all-past-trips"
              onClick={() => {
                setArchiveError('');
                setBulkArchiveDialogOpen(true);
              }}
              startIcon={<ArchiveIcon />}
              sx={{ ...documentTextButtonSx, width: { xs: '100%', sm: 'auto' } }}
            >
              {t('trip.archiveDialog.bulkAction')}
            </Button>
          )}
          {!showArchived && (
            <Button onClick={() => navigate('/trips/create')} sx={{ ...documentSolidButtonSx, width: { xs: '100%', sm: 'auto' } }}>
              {t('trip.createTrip')}
            </Button>
          )}
        </Box>
        </Box>

      {archiveNotice && (
        <Alert severity="success" sx={{ mt: 2 }} onClose={() => setArchiveNotice('')}>
          {archiveNotice}
        </Alert>
      )}

      {!showArchived && <EmailForwardingCard compact />}

      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 1, sm: 2.5 },
        flexWrap: 'wrap',
        py: 2,
        borderBottom: `1px solid ${DOCUMENT.rule}`,
      }}>
        <Box sx={{ display: 'flex', gap: 0.9, flexWrap: 'wrap' }}>
          {[
            { type: 'total', key: 'all', label: t('trip.filterAll'), value: searchedTrips.length },
            { type: 'upcoming', key: 'upcoming', label: t('trip.stats.upcoming'), value: searchedTrips.filter((trip) => getTripStatus(trip).status === 'upcoming').length },
            { type: 'inProgress', key: 'current', label: t('trip.stats.inProgress'), value: searchedTrips.filter((trip) => getTripStatus(trip).status === 'current').length },
            { type: 'completed', key: 'past', label: t('trip.stats.completed'), value: searchedTrips.filter((trip) => getTripStatus(trip).status === 'past').length },
          ].filter((stat) => stat.value > 0 || stat.type === 'total').map((stat) => {
            const selected = statusFilter === stat.key;
            return (
              <Button
                key={stat.type}
                aria-pressed={selected}
                onClick={() => handleStatClick(stat.type)}
                sx={{
                  minWidth: 0,
                  borderRadius: 0,
                  border: `1px solid ${selected ? DOCUMENT.oxblood : DOCUMENT.ruleHard}`,
                  bgcolor: selected ? DOCUMENT.oxblood : 'transparent',
                  color: selected ? DOCUMENT.ivory : DOCUMENT.ink2,
                  fontSize: 12,
                  letterSpacing: 0,
                  textTransform: 'none',
                  px: 1.6,
                  py: 0.75,
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: selected ? DOCUMENT.nav : 'rgba(74,14,21,0.06)',
                    borderColor: selected ? DOCUMENT.nav : DOCUMENT.ruleHard,
                    boxShadow: 'none',
                  },
                }}
              >
                {stat.label}
                <Box component="span" sx={{ ml: 0.75, color: selected ? '#D6BE93' : DOCUMENT.ink3, fontVariantNumeric: 'tabular-nums' }}>
                  {stat.value}
                </Box>
              </Button>
            );
          })}
        </Box>
        <TextField
          size="small"
          type="search"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          placeholder={t('trip.searchPlaceholder')}
          inputProps={{ 'aria-label': t('trip.searchPlaceholder'), 'data-testid': 'my-trips-search' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: DOCUMENT.ink3 }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton size="small" aria-label={t('trip.clearSearch')} onClick={() => setSearchQuery('')} edge="end">
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            width: { xs: '100%', sm: 280 },
            '& .MuiOutlinedInput-root': {
              borderRadius: 0,
              bgcolor: DOCUMENT.surface,
              fontSize: 13,
              '& fieldset': { borderColor: DOCUMENT.ruleHard },
            },
            '& input[type="search"]::-webkit-search-cancel-button': { display: 'none' },
          }}
        />
        <Typography sx={{ ml: { sm: 'auto' }, width: { xs: '100%', sm: 'auto' }, fontSize: 12.5, color: DOCUMENT.ink3 }}>
          {t('trip.newHere')}{' '}
          <Button
            onClick={() => {
              setShowHowItWorks((open) => !open);
              setTimeout(() => {
                document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 50);
            }}
            sx={{
              ...documentTextButtonSx,
              textTransform: 'none',
              letterSpacing: 0,
              fontSize: 12.5,
              verticalAlign: 'baseline',
              p: 0,
              minWidth: 0,
              borderBottom: '1px solid transparent',
            }}
          >
            {t('trip.seeHowTripsWork')}
          </Button>
        </Typography>
      </Box>

      {showHowItWorks && (
        <Box id="how-it-works" sx={{ mt: 3, mb: 1, p: { xs: 2.5, md: 3 }, bgcolor: DOCUMENT.surface, border: `1px solid ${DOCUMENT.ruleHard}` }}>
          <Typography sx={{ ...documentEyebrowSx, mb: 2 }}>
            {t('trip.howItWorksTitle')}
          </Typography>
          <Grid container spacing={{ xs: 1.5, md: 2 }}>
            {[
              { step: 1, title: t('trip.howItWorks.createTitle'), desc: t('trip.howItWorks.createDesc'), action: () => navigate('/trips/create') },
              { step: 2, title: t('trip.howItWorks.buildTitle'), desc: t('trip.howItWorks.buildDesc'), action: null },
              { step: 3, title: t('trip.howItWorks.inviteTitle'), desc: t('trip.howItWorks.inviteDesc'), action: null },
              { step: 4, title: t('trip.howItWorks.exploreTitle'), desc: t('trip.howItWorks.exploreDesc'), action: () => navigate('/deals') },
            ].map((item) => (
              <Grid item xs={12} sm={6} md={3} key={item.step}>
                <Box
                  onClick={item.action || undefined}
                  sx={{ cursor: item.action ? 'pointer' : 'default', '&:hover': item.action ? { color: DOCUMENT.oxblood } : {} }}
                >
                  <Typography sx={{ fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: DOCUMENT.accent, mb: 0.75 }}>
                    {String(item.step).padStart(2, '0')}
                  </Typography>
                  <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 20, color: DOCUMENT.ink, mb: 0.5 }}>
                    {item.title}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3, lineHeight: 1.5 }}>
                    {item.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Pending invitation cards */}
      {!showArchived && pendingInvites.length > 0 && (
        <Box sx={{ mt: 5, mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.75, pb: 1.5, mb: 2.5, borderBottom: `1px solid ${DOCUMENT.ruleHard}` }}>
            <Typography component="h2" sx={{ fontFamily: DOCUMENT.display, fontWeight: 400, fontSize: 23, color: DOCUMENT.ink }}>
              {t('trip.sections.pendingInvitations', 'Pending Invitations')}
            </Typography>
            <Typography sx={{ fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: DOCUMENT.ink3 }}>
              {pendingInvites.length}
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {pendingInvites.map((invite) => (
              <Grid item xs={12} sm={6} md={4} key={invite.id}>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: `2px solid ${theme?.palette?.primary?.main || '#591716'}`,
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 8px 25px rgba(89, 23, 22, 0.15)',
                      transform: 'translateY(-2px)',
                    }
                  }}
                >
                  {/* Header banner */}
                  <Box sx={{
                    background: `linear-gradient(135deg, ${theme?.palette?.primary?.main || '#591716'} 0%, ${theme?.palette?.primary?.dark || '#3d0f0e'} 100%)`,
                    color: 'white',
                    px: 2.5,
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}>
                    <PersonAddIcon sx={{ fontSize: 20 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                      {t('trip.invitationReceived', 'Trip Invitation')}
                    </Typography>
                  </Box>

                  {/* Trip info */}
                  <Box sx={{ p: 2.5 }}>
                    {/* Trip name */}
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: '1.1rem', sm: '1.2rem' },
                        mb: 1.5,
                        lineHeight: 1.3,
                        color: theme?.palette?.text?.primary || '#111',
                      }}
                    >
                      {invite.title}
                    </Typography>

                    {/* Invited by */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <PersonIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {t('trip.invitedBy', 'Invited by')} <strong>{invite.invited_by_name || invite.creator_name}</strong>
                      </Typography>
                    </Box>

                    {/* Destination */}
                    {invite.destination && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <LocationIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          {invite.destination}
                        </Typography>
                      </Box>
                    )}

                    {/* Dates */}
                    {invite.start_date && invite.end_date && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <CalendarTodayIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          {format(new Date(invite.start_date), 'MMM d, yyyy')} — {format(new Date(invite.end_date), 'MMM d, yyyy')}
                        </Typography>
                      </Box>
                    )}

                    {/* Description preview */}
                    {invite.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 2,
                          lineHeight: 1.5,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          fontSize: '0.85rem',
                        }}
                      >
                        {invite.description}
                      </Typography>
                    )}

                    {/* Action buttons */}
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <Button
                        variant="contained"
                        fullWidth
                        disabled={inviteActionLoading}
                        onClick={() => handleAcceptInvite(invite.id)}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 700,
                          py: 1.2,
                          fontSize: '0.9rem',
                          background: `linear-gradient(135deg, ${theme?.palette?.primary?.main || '#591716'} 0%, ${theme?.palette?.primary?.dark || '#3d0f0e'} 100%)`,
                          boxShadow: '0 4px 12px rgba(89, 23, 22, 0.3)',
                          '&:hover': {
                            background: `linear-gradient(135deg, ${theme?.palette?.primary?.dark || '#3d0f0e'} 0%, ${theme?.palette?.primary?.main || '#591716'} 100%)`,
                            transform: 'translateY(-1px)',
                            boxShadow: '0 6px 16px rgba(89, 23, 22, 0.4)',
                          }
                        }}
                      >
                        {t('trip.acceptInvitation', 'Accept')}
                      </Button>
                      <Button
                        variant="outlined"
                        fullWidth
                        disabled={inviteActionLoading}
                        onClick={() => handleRejectInvite(invite.id)}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          py: 1.2,
                          fontSize: '0.9rem',
                          borderColor: theme?.palette?.error?.main || '#d32f2f',
                          color: theme?.palette?.error?.main || '#d32f2f',
                          '&:hover': {
                            borderColor: theme?.palette?.error?.dark || '#b71c1c',
                            backgroundColor: 'rgba(211, 47, 47, 0.04)',
                          }
                        }}
                      >
                        {t('trip.rejectInvitation', 'Decline')}
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
          {inviteActionError && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{inviteActionError}</Alert>
          )}
        </Box>
      )}

      {loading ? (
        <Loading />
      ) : error ? (
        <ErrorState message={error} onRetry={handleRetry} />
      ) : trips.length === 0 ? (
        <EmptyState
        title={t('trip.empty.noTrips')}
        description={t('trip.empty.noTripsDesc')}
        actionText={t('trip.createTrip')}
        onAction={() => setCreateModalOpen(true)}
        />
      ) : (
        <Box id="trips-section">
          {/* Active Filter Indicator */}
          {statusFilter !== 'all' && (
            <Box sx={{ mt: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip
                label={`${t('trip.filteredBy')}: ${statusFilter === 'upcoming' ? t('trip.stats.upcoming') : statusFilter === 'current' ? t('trip.stats.inProgress') : t('trip.stats.completed')}`}
                onDelete={() => setStatusFilter('all')}
                sx={{
                  borderRadius: 0,
                  bgcolor: DOCUMENT.oxblood,
                  color: DOCUMENT.ivory,
                  '& .MuiChip-deleteIcon': { color: DOCUMENT.ivory },
                }}
              />
            </Box>
          )}
          
          {isSearching && filterTripsByStatus(searchedTrips).length === 0 && (
            <Box data-testid="my-trips-no-results" sx={{ py: 6, textAlign: 'center' }}>
              <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 20, color: DOCUMENT.ink }}>
                {t('trip.noSearchResults', { query: deferredSearchQuery.trim() })}
              </Typography>
              <Button onClick={() => setSearchQuery('')} sx={{ ...documentTextButtonSx, mt: 1.5 }}>
                {t('trip.clearSearch')}
              </Button>
            </Box>
          )}
          {!isSearching && showArchived && filterTripsByStatus(searchedTrips).length === 0 && (
            <Box data-testid="my-trips-empty-archived" sx={{ py: 6, textAlign: 'center' }}>
              <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 20, color: DOCUMENT.ink }}>
                {t('trip.emptyArchived')}
              </Typography>
              <Typography sx={{ fontSize: 14, color: DOCUMENT.ink3, mt: 1 }}>
                {t('trip.emptyArchivedHint')}
              </Typography>
              <Button onClick={() => setShowArchived(false)} sx={{ ...documentTextButtonSx, mt: 1.5 }}>
                {t('trip.showActive')}
              </Button>
            </Box>
          )}
          {renderTripSection(t('trip.sections.comingUp', { months: COMING_UP_WINDOW_MONTHS }), filterTripsByStatus(tripSections.comingUp))}
          {renderTripSection(t('trip.sections.tripsYouCreated'), [...filterTripsByStatus(tripSections.created)].sort((a, b) => new Date(b.start_date) - new Date(a.start_date)))}
          {renderTripSection(t('trip.sections.tripsYouWereInvitedTo'), [...filterTripsByStatus(tripSections.invited)].sort((a, b) => new Date(b.start_date) - new Date(a.start_date)))}
          <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3, py: 3.25, pb: 8.75, lineHeight: 1.7 }}>
            {t('trip.retentionNote')}
          </Typography>
        </Box>
      )}

      {/* Menu for trip actions */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl) && canShowTripMenu(selectedTrip)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: THEME_RADIUS.small,
            boxShadow: THEME_SHADOWS.medium,
            border: '1px solid rgba(0, 0, 0, 0.05)',
          }
        }}
      >
        {selectedTrip?.user_role === 'creator' && !showArchived && canEditTripDetails(selectedTrip, { isCreator: true }) && (
          <MenuItem onClick={() => {
            setEditingTripId(selectedTrip?.id);
            setEditModalOpen(true);
            handleMenuClose();
          }}>
            {t('trip.editTrip')}
          </MenuItem>
        )}
        {selectedTrip?.user_role === 'creator' && !showArchived && (
          <MenuItem onClick={() => {
            setPublishTrip(selectedTrip);
            setPublishGuideOpen(true);
            handleMenuClose();
          }}>
            <ListItemText>{t('publishGuide.publish')}</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => {
          setArchiveError('');
          setArchiveDialogOpen(true);
          setAnchorEl(null);
        }}>
          {showArchived ? t('trip.restoreTrip') : t('trip.archiveTrip')}
        </MenuItem>
        {selectedTrip?.user_role === 'creator' && !showArchived && (
          <MenuItem onClick={() => {
            setDeleteDialogOpen(true);
            setAnchorEl(null);
          }}>
            {t('trip.deleteTrip')}
          </MenuItem>
        )}
      </Menu>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('trip.deleteDialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('trip.deleteDialog.message')}
          </DialogContentText>
          {deleteError && <Alert severity="error" sx={{ mt: 2 }}>{deleteError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deletingTrip}>{t('trip.deleteDialog.cancel')}</Button>
          <Button onClick={handleDeleteTrip} color="error" variant="contained" disabled={deletingTrip}>
            {deletingTrip ? t('trip.deleteDialog.deleting') : t('trip.deleteDialog.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Archive confirmation dialog */}
      <Dialog open={archiveDialogOpen} onClose={() => !archivingTrip && setArchiveDialogOpen(false)}>
        <DialogTitle>
          {showArchived ? t('trip.archiveDialog.restoreTitle') : t('trip.archiveDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {showArchived
              ? t('trip.archiveDialog.restoreMessage', { title: selectedTrip?.title })
              : t('trip.archiveDialog.message', { title: selectedTrip?.title })}
          </DialogContentText>
          {archiveError && <Alert severity="error" sx={{ mt: 2 }}>{archiveError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveDialogOpen(false)} disabled={archivingTrip}>
            {t('trip.archiveDialog.cancel')}
          </Button>
          <Button onClick={handleArchiveTrip} variant="contained" disabled={archivingTrip}>
            {archivingTrip
              ? (showArchived ? t('trip.archiveDialog.restoring') : t('trip.archiveDialog.archiving'))
              : (showArchived ? t('trip.restoreTrip') : t('trip.archiveTrip'))}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkArchiveDialogOpen}
        onClose={() => !archivingTrip && setBulkArchiveDialogOpen(false)}
      >
        <DialogTitle>{t('trip.archiveDialog.bulkTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('trip.archiveDialog.bulkMessage', { count: pastArchivableCount })}
          </DialogContentText>
          {archiveError && <Alert severity="error" sx={{ mt: 2 }}>{archiveError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkArchiveDialogOpen(false)} disabled={archivingTrip}>
            {t('trip.archiveDialog.cancel')}
          </Button>
          <Button
            onClick={handleBulkArchivePastTrips}
            variant="contained"
            disabled={archivingTrip || pastArchivableCount === 0}
            data-testid="confirm-archive-all-past-trips"
          >
            {archivingTrip ? t('trip.archiveDialog.archiving') : t('trip.archiveDialog.bulkAction')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Trip Modal */}
      <TripModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        mode="create"
        onSuccess={() => {
          // Refresh trips after successful creation
          fetchTrips();
        }}
      />

      {/* Edit Trip Modal */}
      <TripModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditingTripId(null);
        }}
        mode="edit"
        tripId={editingTripId}
        onSuccess={() => {
          // Refresh trips after successful edit
          fetchTrips();
        }}
      />

      {/* Partner Details Modal */}
      <Dialog
        open={partnerModalOpen}
        onClose={() => setPartnerModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Trip Managed By Partner
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedTrip && (
            <Box sx={{ py: 2 }}>
              <Paper elevation={0} sx={{ bgcolor: '#f8f9fa', p: 3, borderRadius: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  {isUsableImageUrl(selectedTrip.partner_logo) ? (
                    <PartnerLockup
                      src={selectedTrip.partner_logo}
                      alt={selectedTrip.partner_company_name}
                      banner={isPartnerClientBrand(selectedTrip.branding) ? selectedTrip.branding?.banner : undefined}
                      height={48}
                      maxWidth={180}
                    />
                  ) : (
                    <PartnerLogoMark
                      partner={{ company_name: selectedTrip.partner_company_name }}
                      alt={selectedTrip.partner_company_name || ''}
                      sx={{
                        width: 48,
                        height: 48,
                        flexShrink: 0,
                        bgcolor: DOCUMENT.canvas,
                        border: `1px solid ${DOCUMENT.rule}`,
                        color: DOCUMENT.oxblood,
                        fontSize: 16,
                        fontWeight: 600,
                      }}
                    />
                  )}
                  <Typography variant="h6" sx={{ fontWeight: 600, color: DOCUMENT.ink }}>
                    {selectedTrip.partner_company_name}
                  </Typography>
                </Box>
                
                <Stack spacing={2}>
                  {selectedTrip.partner_contact_name && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Contact Person
                      </Typography>
                      <Typography variant="body1">
                        {selectedTrip.partner_contact_name}
                      </Typography>
                    </Box>
                  )}
                  
                  {selectedTrip.partner_website && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Website
                      </Typography>
                      <Typography variant="body1">
                        <a 
                          href={selectedTrip.partner_website.startsWith('http') ? selectedTrip.partner_website : `https://${selectedTrip.partner_website}`} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          style={{ color: '#667eea', textDecoration: 'none' }}
                        >
                          {selectedTrip.partner_website}
                        </a>
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Paper>

              <Alert severity="info" icon={false} sx={{ mt: 2 }}>
                <Typography variant="body2">
                  {t('myInquiries.contactThroughInquiry')}
                </Typography>
              </Alert>

              <Alert severity="info" icon={false}>
                <Typography variant="body2">
                  This partner is managing the itinerary for this trip on your behalf. 
                  You maintain full ownership and control of the trip, including adding members, 
                  managing expenses, and all other trip features.
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={() => setPartnerModalOpen(false)}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Publish as Travel Guide Dialog */}
      {publishTrip && (
        <PublishGuideDialog
          open={publishGuideOpen}
          onClose={() => {
            setPublishGuideOpen(false);
            setPublishTrip(null);
          }}
          trip={publishTrip}
          tripId={publishTrip.id}
        />
      )}
    </Container>
    </Box>
  );
};

export default Trips; 